import Vue from 'vue';
import { SearchUtils } from '@/js';
import {ApolloClient} from 'apollo-client';
import {HttpLink} from 'apollo-link-http';
import {InMemoryCache, defaultDataIdFromObject} from 'apollo-cache-inmemory';
import VueApollo from 'vue-apollo';
import {setContext} from 'apollo-link-context';
import {withClientState} from 'apollo-link-state';
import {ApolloLink} from 'apollo-link';
import localForage from 'localforage';

import {
  getApiConfig,
  defaultState,
  isDevelopment,
  getVersion,
  getLocalCacheKey
} from '@/js/store';

import {
  WhoAmI,
  stateTypeDefs,
  stateResolvers,
  updateControls,
  updateProviderAuth,
  updateToaster,
  updateSearchVariableFilters,
  Controls,
  ElDefaults,
  ProviderAuth,
  SearchVariableFilters,
  SearchVariables,
  updateSearchVariables,
  replacePosterFrames,
  updateElDefaults
} from '@/gql';
import {CachePersistor} from 'apollo-cache-persist';
import FidAuth from '@/plugins/fidauth';
import {onError} from 'apollo-link-error';
import hasher from 'object-hash'

// checking the network status TODO - something with this
const errorLink = onError(({graphQLErrors, networkError}) => {
  if (graphQLErrors) {
    graphQLErrors.map((rs) => {
      const {message, locations, path} = rs;
      console.log(rs);
      console.log(
        `[GraphQL error]: Message: ${JSON.stringify(
          message
        )}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(
          path
        )}`
      );
      // communicate that auth needs to be redone
      if (
        (path || []).indexOf('VimeoUserInfo') !== -1 &&
        message.match(/Authentication error/)
      ) {
        console.log('setting vimeoreconnect');
        mergeControls({
          vimeoReconnect: true,
        });
      }
    });
  }
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const authLink = setContext(
  (request, {headers}) =>
    new Promise((resolve, reject) => {
      // generate the id token
      FidAuth.getIdToken()
        .then(idToken => {
          resolve({
            headers: {
              ...headers,
              'x-fid-idtoken': idToken ? `${idToken}` : '',
              'x-fid-apikey': getApiConfig().apiKey,
              // normally we wont be sending proxy requests from the client
              // its either the logged in user or no logged in user
              'x-fid-proxy': '',
            },
          });
        })
        .catch(err => reject(err));
    })
);

// TODO need to look at using apollo link ws here ? might work better on slower connections
// can alos use apollo-link for wrapping responses in timing and other info
const httpLink = new HttpLink({
  // URL to graphql server, you should use an absolute URL here
  uri: getApiConfig().uri,
});

// initialize apollo cache
// some local state that dont have ids
const cache = new InMemoryCache({
  dataIdFromObject: object => {
    if (!object) {
      console('calling dataidfrom object with null object ', object);
      return defaultDataIdFromObject(object);
    }
    switch (object.__typename) {
      case 'Filter':
        return hasher(
          object.__typename,
          object.value,
          object.valueKey,
          object.op
        );

      case 'SearchVariableFilters':
        return hasher(object.__typename, object.SearchFilters);

      case 'SearchFilter':
        return hasher(
          object.__typename,
          object.value,
          object.itemType
        );

      case 'FilmMasterPosterFrame':
        return object.artworkID;

      case 'ElDefault':
        return object.itemType

      case 'ElDefaultInclude':
        return object.key

      default:
        return defaultDataIdFromObject(object);
    }
  },
});


// use apollo for state management
const stateLink = withClientState({
  cache,
  defaults: defaultState,
  typeDefs: stateTypeDefs,
});

// create the apollo client
const clientOptions = {
  link: ApolloLink.from([stateLink, errorLink, authLink, httpLink]),
  cache,
  connectToDevTools: isDevelopment,
  resolvers: stateResolvers,
};

// this would be for localhost - we dont need cors
if (getApiConfig().noCors) {
  clientOptions.fetchOptions = {
    mode: 'no-cors',
  };
}

export const persistor = new CachePersistor({
  cache,
  storage: localForage,    //window.localStorage, //localforage
  key: getLocalCacheKey(),
  maxSize: 10 * 1024 * 1024,
})


// need this elsewhere occassionally
export const apolloClient = new ApolloClient(clientOptions);

// install the vue plugin
Vue.use(VueApollo);

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
  defaultOptions: {
    $query: {
      loadingKey: 'loading',
      fetchPolicy: 'cache-first',
    },
  },
});

// use this for mutating server side as it has to follow up with a cache update
export const mutate = ({variables, mutation}) => {
  return apolloClient.mutate({
    mutation,
    variables,
    // eslint-disable-next-line
    update: (store, {data}) => {
      // as long as the mutation returns the same query
      // as the original request, then there's no need
      // to play around with cache
      // if it doesn't then we'd need to refresh cache post mutation here
      // just leaving the unused arguments for future convenience
    },
  });
};

// use this for mutating local state
const stateMutate = ({variables, mutation}) => {
  return apolloClient.mutate({
    mutation,
    variables,
  });
};

// update the searchvariable filter
export const searchVariableFilterMutate = value =>
  stateMutate({variables: {value}, mutation: updateSearchVariableFilters});
export const updateAuth = value =>
  stateMutate({variables: {value}, mutation: updateProviderAuth});
export const searchVariablesMutate = value =>
  stateMutate({variables: {value}, mutation: updateSearchVariables});
export const elDefaultsMutate = value =>
  stateMutate({variables: {value}, mutation: updateElDefaults});

// general for merging state
const mergeState = ({merge, mutation}) =>
  stateMutate({variables: {merge}, mutation});

// general for removing __typename from objects
export const removeTypename = ob =>
  SearchUtils.removeProperties({ob, skipKeys: ['__typename'], recursive: true});


export const mergeControls = merge =>
  mergeState({
    mutation: updateControls,
    merge,
  });

export const mergeProviderAuth = merge =>
  mergeState({
    mutation: updateProviderAuth,
    merge,
  });

export const mergeToaster = merge =>
  mergeState({
    mutation: updateToaster,
    merge,
  });
export const mergeSearchVariables = merge =>
  mergeState({
    mutation: updateSearchVariables,
    merge,
  });
// report an error
export const showToaster = ({color, error, header, type, message}) => {
  if (error) {
    console.log('toasting error', {
      error,
      message: `${header ? header + ':' : ''}${message}`,
      type,
    });
  }
  return mergeToaster({
    message,
    type,
    color: color || type || 'accent',
    showToaster: true,
  });
};

// TODO clear apollo cache on user change
export const clearPersistentCache = () => {
  return persistor.purge().catch(error => {
    showToaster({
      header: 'persistent cache purge error',
      type: 'error',
      message: `failed to clear persistor on user change`,
      error,
    });
  });
};

// clear cache
export const clearCache = () => {
  return apolloClient.resetStore(error => {
    showToaster({
      header: 'in memory cache purge error',
      type: 'error',
      message: `failed to clear persistor on user change`,
      error,
    });
  });
};

// refresh store - clears persistent and in memory cache
// this is not exctly perfect as it clears both local state and regular cache
// so it means that we still have remnants fo search results
// by restoring the defaults. TODO - find a way of mutating the local state
// back again after clearing everything
export const clearAllStores = () => {
  // turn that off for a bit
  persistor.pause();

  // first job is to get all local state
  // TODO - add ElDefaults
  return Promise.all(
    [Controls, ProviderAuth, SearchVariableFilters, SearchVariables, ElDefaults].map(
      query => apolloClient.query({query})
    )
  ).then(states => {
    // so that's what we have at the moment safely tucked away
    const [
      controls,
      providerAuth,
      searchVariableFilters,
      searchVariables,
      elDefaults
    ] = states;

    // clear it all
    return (
      apolloClient.clearStore()
        .then(()=>clearPersistentCache())
        .then(() => clearCache())
        .then(() => persistor.resume())

        // a clean store with defaults set up
        .then(() => stateLink.writeDefaults())

        // restore the local state, having cleared the remote stuff
        .then(() => {

          return Promise.all([
            searchVariableFilterMutate(
              searchVariableFilters.data.SearchVariableFilters
            ),
            updateAuth(providerAuth.data.ProviderAuth),
            mergeSearchVariables(searchVariables.data.SearchVariables),
            mergeControls(controls.data.Controls),
            elDefaultsMutate(elDefaults.data.ElDefaults)
          ])
        })
    );
  });
};

// this gets db info about the currently logged in userh
export const whoAmI = () => {
  return apolloClient.query({
    query: WhoAmI,
    fetchPolicy: 'no-cache',
  });
};

// this one replaces the master posterframes from a slides object
export const mutateReplaceMasterPosterFrames = ({slides, filmMaster}) => {
  const input = {
    filmMasterID: filmMaster.id,
    runID: new Date().getTime().toString(32) + '.' + getVersion().text,
    // trim slides to just what we need
    slides: slides.map(f => ({
      artworkTypeID: f.artworkTypeID,
      defaultPosterFrame: f.defaultPosterFrame,
      frameNumber: f.frameNumber,
      posterFrameOrder: f.posterFrameOrder,
      storyFrameOrder: f.storyFrameOrder,
      files: f.files.map(g => ({
        heightPx: g.heightPx,
        original: g.original,
        path: g.path,
        targetName: g.targetName,
        widthPx: g.widthPx,
        imageType: g.imageType,
      })),
    })),
  };
  return apolloClient.mutate({
    mutation: replacePosterFrames,
    variables: {
      input,
    },
  });
};
